/* eslint-disable camelcase */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import type { LearnerProfile_PatchFailure, LearnerProfile_PatchSuccess } from '__generated__/graphql-types-do-not-use';

import { Button, Dialog, InlineNotification, TextField, Typography2 } from '@coursera/cds-core';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import LearnerProfilePatchSlugMutation from 'bundles/account-profile/components/recruiting-profile-viewer/queries/LearnerProfilePatchProfileSlugMutation.graphql';
import type {
  LearnerProfile_PatchProfileSlugMutation,
  LearnerProfile_PatchProfileSlugMutationVariables,
} from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/LearnerProfilePatchProfileSlugMutation';
import {
  SLUG_MAX_COUNT,
  SLUG_MAX_LENGTH,
  URL_PROFILE_SLUG_PATH,
  VALIDATION_MESSAGES,
  generateDefaultSlug,
  getValidationLabel,
  isValidSlug,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/profileSlugUtils';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  cont: () => css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-300);
  `,
  // required to fix css component border issues when using the prefix prop
  removeInheritedStyles: css`
    input {
      margin-bottom: 0;
      border: 0;
      box-shadow: none;
      background: transparent;
    }

    label {
      margin-bottom: 0;
    }

    input[aria-invalid='true']:invalid:focus {
      box-shadow: none;
    }
  `,
};

type Props = {
  isOpen: boolean;
  // callback accepts new slugs to update state
  onClose: (opts: { closeDialog?: boolean; message?: string }) => void;
  currentSlug?: string;
};

const EditProfileLinkDialog = ({ isOpen, onClose, currentSlug }: Props) => {
  const { profileState = {}, updateLearnerProfile } = useContext(ProfileContext);
  const [errors, setErrors] = useState<string[]>([]);
  const [value, setValue] = useState(currentSlug || generateDefaultSlug(profileState));
  const { HeadingGroup, Content, Actions } = Dialog;
  const slugCount = profileState.slugs?.length || 0;
  const disabled = errors.length > 0 || value === currentSlug;

  const [insertMutation] = useMutation<
    LearnerProfile_PatchProfileSlugMutation,
    LearnerProfile_PatchProfileSlugMutationVariables
  >(LearnerProfilePatchSlugMutation, {
    context: {
      clientName: 'gatewayGql',
    },
  });

  const insertSlug = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    async (slug: string) => {
      try {
        const result = await insertMutation({
          variables: {
            input: {
              profileSlug: slug,
            },
          },
        });

        if (
          result?.errors?.length ||
          result.data?.LearnerProfile_PatchProfileSlug.__typename === 'LearnerProfile_PatchFailure'
        ) {
          const failure = result.data?.LearnerProfile_PatchProfileSlug as LearnerProfile_PatchFailure;
          setErrors([...(result?.errors || []), ...failure.errors].map((e) => e.message));
          return;
        }

        if (result.data?.LearnerProfile_PatchProfileSlug.__typename === 'LearnerProfile_PatchSuccess') {
          const success = result.data?.LearnerProfile_PatchProfileSlug as LearnerProfile_PatchSuccess;

          setErrors([]);

          // update state only instead of refreshing the profile because we got the most recent LearnerProfile_Profile from the mutation result
          await updateLearnerProfile({ slugs: success?.profile?.slugs }, true);

          // close the edit form and display the success message
          onClose({ closeDialog: false, message: _t('Custom link saved successfully') });
          return;
        }

        throw new Error('invalid_response');
      } catch (e) {
        Sentry.captureException(e);
        setErrors([VALIDATION_MESSAGES.UNKNOWN_ERROR]);
      }
    },
    [insertMutation, setErrors, onClose, updateLearnerProfile]
  );

  // evaluate slug for real time validation text
  useEffect(() => {
    if (isValidSlug(value)) {
      setErrors([]);
    } else {
      setErrors((state) => [
        ...state.filter((e) => e !== VALIDATION_MESSAGES.SLUG_INVALID_FORMAT),
        VALIDATION_MESSAGES.SLUG_INVALID_FORMAT,
      ]);
    }
  }, [value, setErrors]);

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose({ closeDialog: true })}
      width="large"
      variant="standard"
      aria-describedby="dialog-content"
    >
      <HeadingGroup
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error: next-line
        supportText={
          <Typography2 component="p" variant="bodyPrimary">
            {_t(
              'You can customize your profile link to make it more readable or to match your own branding. The link can include dashes “-” to separate content, but other symbols or special characters aren’t supported.'
            )}
          </Typography2>
        }
      >
        {_t('Customize your link')}
      </HeadingGroup>
      <Content id="dialog-content">
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking
          // not tracked in eventing v3
          trackingName="profile_link_edit_dialog"
          requireFullyVisible={false}
        >
          <div css={styles.cont}>
            <TextField
              css={styles.removeInheritedStyles}
              fullWidth
              label={_t('Profile Link')}
              prefix={URL_PROFILE_SLUG_PATH}
              onChange={(e) => setValue(e.target.value.toLowerCase())}
              validationStatus={errors.length ? 'error' : undefined}
              validationLabel={getValidationLabel(errors)}
              value={value}
              onFocus={(event) => event.target.select()}
              maxLength={SLUG_MAX_LENGTH}
            />
            {!disabled && slugCount > SLUG_MAX_COUNT - 4 && (
              <InlineNotification severity="warning">
                <div>
                  {SLUG_MAX_COUNT - (slugCount + 1) > 0
                    ? _t(
                        'You can customize your link up to #{max} times. If you save this change, then you’ll have only #{remaining} more left.',
                        { max: SLUG_MAX_COUNT, remaining: SLUG_MAX_COUNT - (slugCount + 1) }
                      )
                    : _t(
                        'You’ve reached the maximum number of times that you can customize your link. If you save this change, you won’t be able to customize your profile link again in the future.',
                        { max: SLUG_MAX_COUNT }
                      )}
                </div>
              </InlineNotification>
            )}
          </div>
        </TrackedDiv>
      </Content>
      <Actions style={{ display: 'flex', gap: '15px' }}>
        <Button
          variant="primary"
          onClick={() => insertSlug(value)}
          component={TrackedButton}
          // not tracked in eventing v3
          trackingName="profile_close_public_share_profile"
          requireFullyVisible={false}
          withVisibilityTracking={true}
          disabled={disabled}
        >
          {_t('Save')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => onClose({ closeDialog: false })}
          component={TrackedButton}
          // not tracked in eventing v3
          trackingName="profile_close_public_share_profile"
          requireFullyVisible={false}
          withVisibilityTracking={true}
        >
          {_t('Cancel')}
        </Button>
      </Actions>
    </Dialog>
  );
};

export default EditProfileLinkDialog;
